import { Button } from "@package/subframe/components/Button";
import { IconWithBackground } from "@package/subframe/components/IconWithBackground";
import { Loader } from "@package/subframe/components/Loader";
import { FeatherCheck, FeatherLoader, FeatherWifiOff } from "@subframe/core";
import { format } from "date-fns";
import { useCallback, useRef, useState } from "react";

import type { LogEntryResponseDto } from "@/generated/model";
import type { LogEntryWithSync } from "@/hooks/use-offline-log-entries";

import { LogCheckConfirmationModal } from "./LogCheckConfirmationModal";

const GARDEN_SUPERVISION_CONFIRMATION_TEXT = [
  "I confirm that I have checked each child. I have made eye contact with each one; they are happy and safe. I have conducted a headcount, and all children are accounted for. I have visually checked the garden for hazards, and I am happy that the garden is safe for play.",
];

interface GardenSupervisionViewProps {
  /** Today's garden checks (offline-merged, most-recent-first), pre-filtered to this log type by the parent. */
  checks: Array<LogEntryResponseDto | LogEntryWithSync>;
  isLoading: boolean;
  isOffline: boolean;
  /** Records one fieldless garden check. Owned by the parent so the header count and this list share one source of truth. */
  onRecordCheck: () => Promise<void>;
}

export default function GardenSupervisionView({
  checks,
  isLoading,
  isOffline,
  onRecordCheck,
}: GardenSupervisionViewProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const debounceRef = useRef(false);

  const openConfirm = useCallback(() => {
    if (debounceRef.current) return;
    debounceRef.current = true;
    setTimeout(() => {
      debounceRef.current = false;
    }, 2000);
    setConfirmOpen(true);
  }, []);

  const confirmAndLog = useCallback(async () => {
    setConfirming(true);
    try {
      await onRecordCheck();
      setConfirmOpen(false);
    } finally {
      setConfirming(false);
    }
  }, [onRecordCheck]);

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Offline banner */}
      {isOffline && (
        <div className="flex w-full items-center gap-3 rounded-sm bg-warning-100 px-4 py-3">
          <IconWithBackground
            variant="warning"
            size="medium"
            icon={<FeatherWifiOff />}
            square={true}
          />
          <span className="text-body font-body text-warning-800">
            You&apos;re offline. Checks will sync when reconnected.
          </span>
        </div>
      )}

      {/* Record check */}
      <div className="flex w-full flex-col items-center gap-3 rounded-xl border border-neutral-200 bg-neutral-50 p-6">
        <Button
          icon={<FeatherCheck />}
          onClick={openConfirm}
          variant="brand-primary"
          size="large"
          className="w-full max-w-xs"
        >
          Record garden welfare check
        </Button>
      </div>

      {/* Today's checks */}
      <div className="flex w-full flex-col gap-3">
        <span className="text-body-bold font-body-bold text-default-font">
          Today&apos;s checks
        </span>

        {isLoading ? (
          <div className="flex w-full items-center justify-center py-8">
            <Loader size="medium" />
          </div>
        ) : checks.length === 0 ? (
          <div className="flex w-full flex-col items-center justify-center gap-1 py-8">
            <span className="text-body font-body text-subtext-color">
              No checks recorded yet
            </span>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-2">
            {checks.map((entry) => (
              <CheckEntryRow key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>

      <LogCheckConfirmationModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Confirm garden welfare check"
        confirmationText={GARDEN_SUPERVISION_CONFIRMATION_TEXT}
        onConfirm={confirmAndLog}
        isSubmitting={confirming}
      />
    </div>
  );
}

function CheckEntryRow({
  entry,
}: {
  entry: LogEntryResponseDto | LogEntryWithSync;
}) {
  const isPending =
    "localSyncState" in entry && entry.localSyncState === "pending";
  const recordedByName =
    [entry.recordedBy.firstName, entry.recordedBy.lastName]
      .filter(Boolean)
      .join(" ") || entry.recordedBy.email;

  return (
    <div className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-100">
        <FeatherCheck className="h-4 w-4 text-green-600" />
      </div>
      <div className="flex grow flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            {format(new Date(entry.timestamp), "HH:mm")}
          </span>
          {isPending && (
            <FeatherLoader className="h-3 w-3 animate-spin text-subtext-color" />
          )}
        </div>
      </div>
      <span className="text-caption font-caption text-subtext-color">
        {recordedByName}
      </span>
    </div>
  );
}
